import { apiFetch } from "./api";

export type ExportLeadRow = {
  id: number;
  nome: string;
  email: string;
  whatsapp: string;
  origem: string | null;
  status: string;
  prioridade: string;
  notas: string | null;
  created_at: string;
  updated_at: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  booking_status: string | null;
  booking_data: string | null;
  booking_dia_semana: string | null;
  booking_inicio: string | null;
  booking_fim: string | null;
  answer_count: number;
  answers_summary: string | null;
};

type LeadsExportResponse = {
  leads: ExportLeadRow[];
  total: number;
  page: number;
  pageSize: number;
};

const COLUMNS: Array<[keyof ExportLeadRow, string]> = [
  ["id", "ID"],
  ["nome", "Nome"],
  ["email", "E-mail"],
  ["whatsapp", "WhatsApp"],
  ["origem", "Origem"],
  ["status", "Status"],
  ["prioridade", "Prioridade"],
  ["booking_data", "Data agendamento"],
  ["booking_inicio", "Inicio"],
  ["booking_fim", "Fim"],
  ["booking_status", "Status agendamento"],
  ["booking_dia_semana", "Dia semana"],
  ["answer_count", "Qtd. respostas"],
  ["answers_summary", "Resumo respostas"],
  ["notas", "Notas internas"],
  ["utm_source", "UTM source"],
  ["utm_medium", "UTM medium"],
  ["utm_campaign", "UTM campaign"],
  ["created_at", "Criado em"],
  ["updated_at", "Atualizado em"],
];

const COLUMN_WIDTHS = [8, 28, 34, 18, 14, 16, 14, 18, 12, 12, 20, 16, 14, 80, 46, 18, 18, 22, 21, 21];

function protectSpreadsheetValue(value: unknown): string {
  const text = String(value ?? "");
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
}

function csvCell(value: unknown): string {
  const text = protectSpreadsheetValue(value).replace(/"/g, '""');
  return `"${text}"`;
}

function htmlCell(value: unknown): string {
  return protectSpreadsheetValue(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function xmlCell(value: unknown): string {
  return htmlCell(value).replace(/'/g, "&apos;");
}

function downloadFile(filename: string, mime: string, content: string | Uint8Array): void {
  const part =
    typeof content === "string"
      ? content
      : (() => {
          const copy = new ArrayBuffer(content.byteLength);
          new Uint8Array(copy).set(content);
          return copy;
        })();
  const blob = new Blob([part], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function colName(index: number): string {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    out = String.fromCharCode(65 + r) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function sheetXml(rows: ExportLeadRow[]): string {
  const header = COLUMNS.map(([, label], c) => {
    const ref = `${colName(c)}1`;
    return `<c r="${ref}" t="inlineStr" s="1"><is><t>${xmlCell(label)}</t></is></c>`;
  }).join("");

  const body = rows
    .map((row, r) => {
      const rowNumber = r + 2;
      const cells = COLUMNS.map(([key], c) => {
        const ref = `${colName(c)}${rowNumber}`;
        const style = key === "answers_summary" || key === "notas" ? ' s="2"' : "";
        return `<c r="${ref}" t="inlineStr"${style}><is><t>${xmlCell(row[key])}</t></is></c>`;
      }).join("");
      return `<row r="${rowNumber}">${cells}</row>`;
    })
    .join("");
  const lastCol = colName(COLUMNS.length - 1);
  const lastRow = Math.max(1, rows.length + 1);

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <cols>
    ${COLUMN_WIDTHS.map((width, i) => `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`).join("")}
  </cols>
  <sheetData><row r="1">${header}</row>${body}</sheetData>
  <autoFilter ref="A1:${lastCol}${lastRow}"/>
</worksheet>`;
}

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of data) {
    crc ^= byte;
    for (let i = 0; i < 8; i += 1) {
      crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
    }
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function concatBytes(parts: Uint8Array[]): Uint8Array {
  const size = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(size);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

function u16(value: number): Uint8Array {
  return new Uint8Array([value & 0xff, (value >>> 8) & 0xff]);
}

function u32(value: number): Uint8Array {
  return new Uint8Array([
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  ]);
}

function makeZip(files: Array<{ name: string; content: string }>): Uint8Array {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const name = encoder.encode(file.name);
    const content = encoder.encode(file.content);
    const crc = crc32(content);

    const local = concatBytes([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(content.length),
      u32(content.length),
      u16(name.length),
      u16(0),
      name,
      content,
    ]);
    localParts.push(local);

    centralParts.push(
      concatBytes([
        u32(0x02014b50),
        u16(20),
        u16(20),
        u16(0),
        u16(0),
        u16(0),
        u16(0),
        u32(crc),
        u32(content.length),
        u32(content.length),
        u16(name.length),
        u16(0),
        u16(0),
        u16(0),
        u16(0),
        u32(0),
        u32(offset),
        name,
      ]),
    );

    offset += local.length;
  }

  const central = concatBytes(centralParts);
  const end = concatBytes([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(central.length),
    u32(offset),
    u16(0),
  ]);

  return concatBytes([...localParts, central, end]);
}

function buildWorkbook(rows: ExportLeadRow[]): Uint8Array {
  const files = [
    {
      name: "[Content_Types].xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  <Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>
  <Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>
</Types>`,
    },
    {
      name: "_rels/.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>
  <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>
</Relationships>`,
    },
    {
      name: "docProps/core.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <dc:title>Leads UdiSchool</dc:title>
  <dc:creator>UdiSchool</dc:creator>
  <dcterms:created xsi:type="dcterms:W3CDTF">${new Date().toISOString()}</dcterms:created>
</cp:coreProperties>`,
    },
    {
      name: "docProps/app.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties"><Application>UdiSchool Admin</Application></Properties>`,
    },
    {
      name: "xl/workbook.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="Leads" sheetId="1" r:id="rId1"/></sheets>
</workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    {
      name: "xl/styles.xml",
      content: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>
  <fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="3"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1"><alignment wrapText="1" vertical="top"/></xf></cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`,
    },
    {
      name: "xl/worksheets/sheet1.xml",
      content: sheetXml(rows),
    },
  ];

  return makeZip(files);
}

export async function fetchAllLeadsForExport(filters: {
  search?: string;
  status?: string;
  prioridade?: string;
}): Promise<ExportLeadRow[]> {
  const pageSize = 100;
  let page = 1;
  let total = Infinity;
  const rows: ExportLeadRow[] = [];

  while (rows.length < total) {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.status) params.set("status", filters.status);
    if (filters.prioridade) params.set("prioridade", filters.prioridade);
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));

    const result = await apiFetch<LeadsExportResponse>(`/admin/leads?${params.toString()}`, {
      auth: "admin",
    });
    rows.push(...result.leads);
    total = result.total;
    if (result.leads.length === 0) break;
    page += 1;
  }

  return rows;
}

export function exportLeadsCsv(rows: ExportLeadRow[]): void {
  const header = COLUMNS.map(([, label]) => csvCell(label)).join(";");
  const body = rows
    .map((row) => COLUMNS.map(([key]) => csvCell(row[key])).join(";"))
    .join("\r\n");
  downloadFile("udischool-leads.csv", "text/csv;charset=utf-8", `\uFEFF${header}\r\n${body}`);
}

export function exportLeadsExcel(rows: ExportLeadRow[]): void {
  downloadFile(
    "udischool-leads.xlsx",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    buildWorkbook(rows),
  );
}
